import { useEffect, useRef, useState } from 'react'

/**
 * Tracks whether anything has scrolled underneath a sticky bar.
 *
 * Listens in the capture phase so it catches scroll from whichever element actually
 * scrolls: the document on an ordinary page, a pane on a screen like Messages that scrolls
 * its own columns, or the surface above it, as in a full screen dialog.
 *
 * @returns {[React.RefObject, boolean]} A ref for the bar, and whether it has content scrolled under it.
 */
export default function useScrolledUnder() {
  const ref = useRef(null)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const parent = ref.current?.parentElement
    if (!parent) return undefined

    const read = (target) => {
      if (!target || target === document || target === document.documentElement || target === document.body) {
        return window.scrollY > 0
      }

      if (!(target instanceof Element)) return null

      // The scroller is a pane under the bar's parent on a screen that scrolls its own
      // columns, and an ancestor of it in a dialog, whose surface scrolls the bar too.
      return parent.contains(target) || target.contains(parent) ? target.scrollTop > 0 : null
    }

    const onScroll = (event) => {
      const next = read(event.target)
      if (next !== null) setScrolled(next)
    }

    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    setScrolled(window.scrollY > 0)

    return () => document.removeEventListener('scroll', onScroll, { capture: true })
  }, [])

  return [ref, scrolled]
}
